import React, { useState } from 'react';
import {
  Network,
  GitBranch,
  Cpu,
  Code2,
  ChevronDown,
  ChevronUp,
  Sparkles
} from 'lucide-react';
import type { GraphAnalyticsRecommendation, GDSAlgorithmRecommendation } from '../../../types/workflowAnalysis';

interface GraphAnalyticsSectionProps {
  graphAnalytics: GraphAnalyticsRecommendation;
}

const AlgorithmCard: React.FC<{ algorithm: GDSAlgorithmRecommendation; index: number }> = ({
  algorithm,
  index
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Map algorithm names to badge colors
  const getAlgorithmColor = (name: string): string => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('pagerank') || lowerName.includes('centrality')) {
      return 'bg-purple-100 text-purple-700 border-purple-200';
    }
    if (lowerName.includes('louvain') || lowerName.includes('community') || lowerName.includes('label')) {
      return 'bg-blue-100 text-blue-700 border-blue-200';
    }
    if (lowerName.includes('similarity') || lowerName.includes('jaccard')) {
      return 'bg-green-100 text-green-700 border-green-200';
    }
    if (lowerName.includes('path') || lowerName.includes('shortest')) {
      return 'bg-amber-100 text-amber-700 border-amber-200';
    }
    if (lowerName.includes('embedding') || lowerName.includes('node2vec') || lowerName.includes('graphsage')) {
      return 'bg-cyan-100 text-cyan-700 border-cyan-200';
    }
    if (lowerName.includes('link') || lowerName.includes('prediction')) {
      return 'bg-pink-100 text-pink-700 border-pink-200';
    }
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  return (
    <div className="border border-indigo-200 rounded-lg bg-white overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-indigo-50/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-sm font-medium">
            {index + 1}
          </span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getAlgorithmColor(algorithm.algorithm)}`}>
            {algorithm.algorithm}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-indigo-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-indigo-500" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 pt-2 space-y-3 bg-indigo-50/30">
          <div>
            <span className="text-xs font-medium text-indigo-600 uppercase tracking-wide">Purpose</span>
            <p className="text-sm text-neutral-700 mt-1">{algorithm.purpose}</p>
          </div>
          <div>
            <span className="text-xs font-medium text-indigo-600 uppercase tracking-wide">Target Nodes</span>
            <p className="text-sm text-neutral-700 mt-1 font-mono bg-white px-2 py-1 rounded border border-indigo-100">
              {algorithm.target_nodes}
            </p>
          </div>
          <div>
            <span className="text-xs font-medium text-indigo-600 uppercase tracking-wide">Expected Output</span>
            <p className="text-sm text-neutral-700 mt-1">{algorithm.expected_output}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export const GraphAnalyticsSection: React.FC<GraphAnalyticsSectionProps> = ({ graphAnalytics }) => {
  const hasAlgorithms = graphAnalytics.gds_algorithms && graphAnalytics.gds_algorithms.length > 0;
  const hasPatterns = graphAnalytics.graph_patterns && graphAnalytics.graph_patterns.length > 0;
  const hasMLFeatures = graphAnalytics.graph_features_for_ml && graphAnalytics.graph_features_for_ml.length > 0;
  const hasCypherPatterns = graphAnalytics.cypher_patterns && graphAnalytics.cypher_patterns.length > 0;

  if (!hasAlgorithms && !hasPatterns && !hasMLFeatures && !hasCypherPatterns) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center gap-2">
        <div className="p-1.5 bg-indigo-100 rounded-lg">
          <Network className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h4 className="text-lg font-semibold text-neutral-900">Graph Analytics Recommendations</h4>
          <p className="text-sm text-neutral-500">Neo4j Graph Data Science algorithms for deeper insights</p>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* GDS Algorithms - Left Column */}
        {hasAlgorithms && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 pb-2 border-b border-indigo-100">
              <Cpu className="w-4 h-4 text-indigo-600" />
              <h5 className="font-medium text-neutral-800">Recommended GDS Algorithms</h5>
            </div>
            <div className="space-y-2">
              {graphAnalytics.gds_algorithms.map((alg, idx) => (
                <AlgorithmCard key={idx} algorithm={alg} index={idx} />
              ))}
            </div>
          </div>
        )}

        {/* Right Column - Patterns and Features */}
        <div className="space-y-4">
          {/* Graph Patterns */}
          {hasPatterns && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 pb-2 border-b border-indigo-100">
                <GitBranch className="w-4 h-4 text-indigo-600" />
                <h5 className="font-medium text-neutral-800">Graph Patterns</h5>
              </div>
              <div className="space-y-1.5">
                {graphAnalytics.graph_patterns.map((pattern, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 px-3 py-2 bg-white border border-indigo-100 rounded-lg"
                  >
                    <span className="text-indigo-500 text-xs font-medium">{idx + 1}</span>
                    <code className="text-sm text-indigo-800 font-mono">{pattern}</code>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ML Features */}
          {hasMLFeatures && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 pb-2 border-b border-indigo-100">
                <Sparkles className="w-4 h-4 text-indigo-600" />
                <h5 className="font-medium text-neutral-800">Graph Features for ML</h5>
              </div>
              <div className="flex flex-wrap gap-2">
                {graphAnalytics.graph_features_for_ml.map((feature, idx) => (
                  <span
                    key={idx}
                    className="px-2.5 py-1 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-full text-xs text-indigo-700"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Cypher Patterns */}
          {hasCypherPatterns && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 pb-2 border-b border-indigo-100">
                <Code2 className="w-4 h-4 text-indigo-600" />
                <h5 className="font-medium text-neutral-800">Key Cypher Patterns</h5>
              </div>
              <div className="space-y-1.5">
                {graphAnalytics.cypher_patterns.map((pattern, idx) => (
                  <div
                    key={idx}
                    className="px-3 py-2 bg-neutral-900 rounded-lg overflow-x-auto"
                  >
                    <code className="text-sm text-green-400 font-mono whitespace-pre-wrap">{pattern}</code>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Info Note */}
      <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
        <p className="text-sm text-indigo-700">
          <strong>Why Graph Analytics?</strong> Your data will be stored in a Neo4j Knowledge Graph,
          enabling powerful relationship-based analytics beyond traditional SQL queries.
          GDS algorithms can reveal patterns, clusters, and predictions that aren't visible in tabular data.
        </p>
      </div>
    </div>
  );
};

export default GraphAnalyticsSection;
